import type { AllocationInitialValues } from '@oneview/components/~advisor/Trading/Allocation';
import { getInitialValues as getInitialAllocationFormValues } from '@oneview/components/~advisor/Trading/Allocation/AllocationForm';
import type { TradingPreload } from '@oneview/components/~advisor/Trading/Trading/schema';
import { AdvisorMenuId, ClientMenuId, DetailsTabId, QuoteTabId } from '@oneview/constants/ids';
import { advisorMenu, clientMenu } from '@oneview/constants/menus';
import { formatOptionSymbol } from '@oneview/utils';
import { isAdvisor } from '@oneview/utils/branchUtils';
import { Quote } from '@orbis-systems/orbis-api-middleware';
import { dismissAlertDialog, getRouteWithParams, showAlertDialog } from '@orbis-systems/orbis-react-toolkit';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { isString, uniq } from 'lodash';
import { push } from 'redux-first-history';
import type { RootState } from '..';
import { AdvisorMenuRoute, Route } from '../../constants/routes';
import { Tab, TabMeta } from '../../constants/tabs';
import { ThunkAction } from '../utils';
import { selectOpeningTabs, selectTabBar } from './tabBarSelectors';

export const openTab = createAction<{ tab: Tab & Partial<TabMeta>; isGroupUpTabsEnabled: boolean }>('OpenTab');

export const openTabWithHistory =
    (tab: Tab & Partial<TabMeta>): ThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const tabs = selectOpeningTabs(state);
        const isGroupUpTabsEnabled = state.setting.common.global.groupUpTabs;

        if (
            isAdvisor() ||
            !tabs.some((currentTab) => currentTab.siblingTabs.some((siblingTab) => siblingTab.route === tab.route))
        ) {
            dispatch(push(tab.route));
        }

        dispatch(isAdvisor() ? openTab({ tab, isGroupUpTabsEnabled }) : replaceCurrentTab(tab));
    };

export const changeTab = createAction<number>('ChangeTab');

export const changeTabWithHistory =
    (tabIndex: number): ThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const tabs = selectOpeningTabs(state);
        dispatch(push(tabs[tabIndex]!.siblingTabs[tabs[tabIndex]!.activeSiblingTabIndex]!.route));
        dispatch(changeTab(tabIndex));
    };

export const replaceCurrentTab = createAction<Tab & Partial<TabMeta>>('ReplaceCurrentTab');

export const updateTab = createAction<Tab & Partial<TabMeta>>('UpdateTab');

export const popoutTab = createAction<number>('PopoutTab');

export const popinTab = createAction<number>('PoppinTab');

export const closeTab = createAction<number>('CloseTab');

export const setActiveSiblingTabIndex = createAsyncThunk(
    'SetActiveSiblingTabIndex',
    (
        {
            tabIndex,
            siblingTabIndex,
        }: {
            tabIndex: number;
            siblingTabIndex: number;
        },
        { dispatch, getState },
    ) => {
        const state = getState() as RootState;
        const activeTabIndexRoute = selectTabBar(state).tabs[tabIndex]!.siblingTabs[siblingTabIndex]!.route;
        dispatch(push(activeTabIndexRoute));
        return { tabIndex, siblingTabIndex };
    },
);

export const closeSiblingTab = createAsyncThunk(
    'CloseSiblingTab',
    (
        {
            tabIndex,
            siblingTabIndex,
        }: {
            tabIndex: number;
            siblingTabIndex: number;
        },
        { dispatch, getState },
    ) => {
        const state = getState() as RootState;
        const activeTabIndex = selectTabBar(state).activeTabIndex;
        const siblings = selectTabBar(state).tabs[tabIndex]!.siblingTabs;
        const currentActiveTabIndex = selectTabBar(state).tabs[tabIndex]!.activeSiblingTabIndex;
        const isActiveTabClosing = siblingTabIndex === currentActiveTabIndex;
        const newActiveTabIndexRoute = isActiveTabClosing
            ? siblingTabIndex !== 0
                ? siblings[siblingTabIndex - 1]!.route
                : siblings[1]!.route
            : siblings[currentActiveTabIndex]!.route;
        if (isActiveTabClosing && activeTabIndex === tabIndex) {
            dispatch(push(newActiveTabIndexRoute));
        }
        return { tabIndex, siblingTabIndex, newActiveTabIndexRoute };
    },
);

export const closeAllSiblingTabsExcept = createAsyncThunk(
    'CloseAllSiblingTabsExcept',
    (
        {
            tabIndex,
            siblingTabIndex,
        }: {
            tabIndex: number;
            siblingTabIndex: number;
        },
        { dispatch, getState },
    ) => {
        const state = getState() as RootState;
        const activeTabIndex = selectTabBar(state).activeTabIndex;
        const activeTabIndexRoute = selectTabBar(state).tabs[tabIndex]!.siblingTabs[siblingTabIndex]!.route;
        if (location.pathname !== activeTabIndexRoute && activeTabIndex === tabIndex) {
            dispatch(push(activeTabIndexRoute));
        }
        return { tabIndex, siblingTabIndex };
    },
);

export const closeAllSiblingTabsAfter = createAsyncThunk(
    'CloseAllSiblingTabsAfter',
    (
        {
            tabIndex,
            siblingTabIndex,
        }: {
            tabIndex: number;
            siblingTabIndex: number;
        },
        { dispatch, getState },
    ) => {
        const state = getState() as RootState;
        const activeTabIndex = selectTabBar(state).activeTabIndex;
        const activeSiblingIndex = selectTabBar(state).tabs[tabIndex]!.activeSiblingTabIndex;
        const lastTabRoute = selectTabBar(state).tabs[tabIndex]!.siblingTabs[siblingTabIndex]!.route;
        if (activeSiblingIndex > siblingTabIndex && activeTabIndex === tabIndex) {
            dispatch(push(lastTabRoute));
        }
        return { tabIndex, siblingTabIndex };
    },
);

export const closeAllTabs = createAsyncThunk('CloseAllTabs', (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const tabs = selectOpeningTabs(state);
    const nextTab = tabs[0]!;
    dispatch(push(nextTab.siblingTabs[nextTab.activeSiblingTabIndex]!.route));
});

export const closeAllRightTabs = createAsyncThunk('CloseAllRightTabs', (tabIndex: number, { dispatch, getState }) => {
    const state = getState() as RootState;
    const { activeTabIndex } = selectTabBar(state);
    if (tabIndex < activeTabIndex) {
        const tabs = selectOpeningTabs(state);
        const nextTab = tabs[tabIndex]!;
        dispatch(push(nextTab.siblingTabs[nextTab.activeSiblingTabIndex]!.route));
    }
    return tabIndex;
});

export const closeAllTabsExcept = createAsyncThunk('CloseAllTabsExcept', (tabIndex: number, { dispatch, getState }) => {
    const state = getState() as RootState;
    const tabs = selectOpeningTabs(state);
    const nextTab = tabs[tabIndex]!;
    dispatch(push(nextTab.siblingTabs[nextTab.activeSiblingTabIndex]!.route));
    return tabIndex;
});

export const closeTabWithHistory =
    (tabIndex?: number): ThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const activeTabIndex = selectTabBar(state).activeTabIndex;
        const tabs = selectOpeningTabs(state);
        const index = tabIndex !== undefined ? tabIndex : activeTabIndex;
        if (index === activeTabIndex) {
            const nextTab = activeTabIndex === tabs.length - 1 ? tabs[index - 1]! : tabs[index + 1]!;

            dispatch(push(nextTab.siblingTabs[nextTab.activeSiblingTabIndex]!.route));
        }
        dispatch(closeTab(index));
    };

export const dragTab = createAction<{
    sourceIndex: number;
    destinationIndex: number;
}>('DragTab');

export const dragSiblingTab = createAction<{
    sourceIndex: number;
    destinationIndex: number;
    tabIndex: number;
}>('DragTabInGroup');

export const changeClientPage =
    (menuId: ClientMenuId): ThunkAction =>
    (dispatch) => {
        const menu = clientMenu.find((menu) => menu.id === menuId);

        if (menu) {
            dispatch(openTabWithHistory(menu as Tab & Partial<TabMeta>));
        }
    };

export const notFound =
    ({ tabIndex, message = 'Page not found' }: { tabIndex?: number; message?: string }): ThunkAction =>
    (dispatch, getState) => {
        const { activeTabIndex } = selectTabBar(getState());
        dispatch(
            showAlertDialog({
                content: message,
                onPositiveClick: (_, id) => {
                    if (tabIndex === undefined || tabIndex === activeTabIndex) {
                        dispatch(closeTabWithHistory(activeTabIndex));
                    } else {
                        dispatch(closeTab(tabIndex));
                    }

                    dispatch(dismissAlertDialog(id));
                },
            }),
        );
    };

export const openModelDetails =
    (modelId: number, title?: string): ThunkAction =>
    (dispatch) => {
        dispatch(
            openTabWithHistory({
                id: DetailsTabId.Model,
                name: title || 'Models Details',
                route: getRouteWithParams(Route.Model, {
                    modelId,
                }),
            }),
        );
    };

export const openArticlePage =
    (articleId: number, title: string): ThunkAction =>
    (dispatch) => {
        dispatch(
            openTabWithHistory({
                id: DetailsTabId.NewsArticle,
                name: title,
                route: getRouteWithParams(Route.NewsArticle, {
                    articleId,
                }),
            }),
        );
    };

export const openGroupDetails =
    (groupId: number, title?: string): ThunkAction =>
    (dispatch) => {
        dispatch(
            openTabWithHistory({
                id: DetailsTabId.Group,
                name: title || 'Group Details',
                route: getRouteWithParams(Route.Group, {
                    groupId,
                }),
            }),
        );
    };

export const openUserDetails =
    (userId: number, title?: string): ThunkAction =>
    (dispatch) => {
        dispatch(
            openTabWithHistory({
                id: DetailsTabId.User,
                name: title || 'User Details',
                route: getRouteWithParams(Route.User, {
                    userId,
                }),
            }),
        );
    };

export const openAccountDetails =
    (accountId: number, title?: string): ThunkAction =>
    (dispatch) => {
        dispatch(
            openTabWithHistory({
                id: DetailsTabId.Account,
                name: title || 'Account Details',
                route: getRouteWithParams(Route.Account, {
                    accountId,
                }),
            }),
        );
    };

export const openQuoteDetails =
    (symbol: string, quote?: Quote | null, jumpTab?: QuoteTabId): ThunkAction =>
    (dispatch) => {
        dispatch(
            openTabWithHistory({
                id: DetailsTabId.Quote,
                name: quote?.option ? formatOptionSymbol(quote) : symbol,
                route: getRouteWithParams(Route.Quote, {
                    symbol,
                }),
                init:
                    jumpTab !== undefined
                        ? {
                              jumpTo: jumpTab,
                          }
                        : undefined,
            }),
        );
    };

export const openEtfCenter = (): ThunkAction => (dispatch) => {
    dispatch(
        openTabWithHistory({
            id: AdvisorMenuId.InsightsEtfCenter,
            name: 'ETF Center',
            route: AdvisorMenuRoute.InsightsEtfCenter,
        }),
    );
};

export const openEarnings = (): ThunkAction => (dispatch) => {
    dispatch(
        openTabWithHistory({
            id: AdvisorMenuId.InsightsEarnings,
            name: 'Earnings',
            route: AdvisorMenuRoute.InsightsEarnings,
        }),
    );
};

export function openAllocation(hasInventoryAccount: boolean): ThunkAction;

export function openAllocation(
    hasInventoryAccount: boolean,
    allocation: AllocationInitialValues['initialValues'],
): ThunkAction;

export function openAllocation(hasInventoryAccount: boolean, symbol: string, accountIds: number[]): ThunkAction;

export function openAllocation(
    hasInventoryAccount: boolean,
    data?: string | AllocationInitialValues['initialValues'],
    accountIds?: number[],
): ThunkAction {
    return (dispatch, getState) => {
        const allocationTab = advisorMenu(hasInventoryAccount || false).find(
            (item) => item.id === AdvisorMenuId.TradingAllocation,
        ) as (Tab & Partial<TabMeta<AllocationInitialValues>>) | undefined;

        if (!allocationTab) {
            return;
        }

        if (data === undefined) {
            dispatch(openTabWithHistory(allocationTab));

            return;
        }

        const openAllocationTab = () => {
            allocationTab.init = {
                initialValues: isString(data)
                    ? {
                          preallocation: true,
                          symbol: data,
                          modelId: 0,
                          orderIds: [],
                          totalShares: 0,
                          totalAllocatedShares: 0,
                          mutualFund: false,
                          blocks: [
                              {
                                  accountIds: uniq(accountIds),
                                  percentage: 100,
                                  shares: 0,
                                  manualLastEdit: 0,
                                  manualShares: {},
                                  manualValues: {},
                                  manualTransactions: {},
                                  ...getInitialAllocationFormValues(),
                              },
                          ],
                      }
                    : data,
            };

            dispatch(openTabWithHistory(allocationTab));
        };

        const openingTabs = selectOpeningTabs(getState());

        if (openingTabs.find((item) => item.id === AdvisorMenuId.TradingAllocation)) {
            dispatch(
                showAlertDialog({
                    content:
                        'There is an allocation tab already opened. Continuing this action will clear your current allocation. Do you want to continue?',
                    positiveButton: 'Yes',
                    negativeButton: 'No',
                    onPositiveClick: (_, id) => {
                        openAllocationTab();
                        dispatch(dismissAlertDialog(id));
                    },
                    onNegativeClick: (_, id) => dispatch(dismissAlertDialog(id)),
                }),
            );
        } else {
            openAllocationTab();
        }
    };
}

export function openTradingTab(hasInventoryAccount: boolean, preload: TradingPreload['preload']): ThunkAction {
    return (dispatch, getState) => {
        const openTradingTab = () => {
            const tab = {
                ...advisorMenu(hasInventoryAccount || false).find(
                    (item) => item.id === AdvisorMenuId.TradingAccountOrModel,
                ),
            } as Tab & Partial<TabMeta<TradingPreload>>;

            tab.init = {
                preload,
            };

            dispatch(openTabWithHistory(tab));
        };

        const openingTabs = selectOpeningTabs(getState());

        if (openingTabs.find((item) => item.id === AdvisorMenuId.TradingAccountOrModel)) {
            dispatch(
                showAlertDialog({
                    content:
                        'There is a trading tab already opened. Continuing this action will clear your current trading. Do you want to continue?',
                    positiveButton: 'Yes',
                    negativeButton: 'No',
                    onPositiveClick: (_, id) => {
                        openTradingTab();
                        dispatch(dismissAlertDialog(id));
                    },
                    onNegativeClick: (_, id) => dispatch(dismissAlertDialog(id)),
                }),
            );
        } else {
            openTradingTab();
        }
    };
}
